// Modal: admin review of an `awaiting_review` intake form.
// Admin reads the lot worker's Tier-1 inspection answers, edits the
// Tier-2 elections (toggle "elect for work"), captures reviewer
// notes, then either approves (no Tier-2 elected) or sends to
// work_in_progress (any elected). Per planning doc §6.2.
//
// Slice 2: when a matching template is supplied, renders per-item
// Tier-1 read-only summary + Tier-2 elect checkboxes. Falls back to
// the Slice-1 JSON-pre-dump + manual elect-count input when the
// form's template_id no longer resolves (orphaned form, or
// pre-Slice-2 row).
//
// Server derives the elected count from the persisted
// tier_2_responses (Slice 2 hardening); the modal's count display is
// a UI preview only.

import { useMemo, useState } from "react";
import {
  reviewFulfillmentIntakeForm,
  type FulfillmentIntakeForm,
  type FulfillmentIntakeTemplate,
} from "@/lib/fulfillment/fulfillmentApi";
import {
  coerceTemplateItems,
  type IntakeTemplateItem,
} from "@/lib/fulfillment/templateItem";
import {
  countElectedTier2,
  emptyTier2Response,
  type Tier2Response,
} from "@/lib/fulfillment/intakeResponseShape";
import {
  buildTier2Map,
  ReviewTier1Section,
  ReviewTier2Section,
} from "./FulfillmentIntakeItemRows";

type Props = {
  form: FulfillmentIntakeForm;
  /** Resolved template (matched by form.template_id). null falls back to the JSON-dump path. */
  template: FulfillmentIntakeTemplate | null;
  onClose: () => void;
  onSaved: () => void;
};

export function FulfillmentIntakeFormReviewModal({ form, template, onClose, onSaved }: Props) {
  const tier1Items = useMemo<IntakeTemplateItem[]>(
    () => (template ? coerceTemplateItems(template.tier_1_items) : []),
    [template]
  );
  const tier2Items = useMemo<IntakeTemplateItem[]>(
    () => (template ? coerceTemplateItems(template.tier_2_items) : []),
    [template]
  );
  const useStructured = !!template && (tier1Items.length > 0 || tier2Items.length > 0);

  const [notes, setNotes] = useState(form.reviewer_notes);
  const [tier2Map, setTier2Map] = useState<Record<string, Tier2Response>>(() =>
    buildTier2Map(tier2Items, form.tier_2_responses)
  );
  const [legacyElectedCount, setLegacyElectedCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const electedCountPreview = useStructured
    ? countElectedTier2(tier2Map)
    : legacyElectedCount;
  const willRouteTo = electedCountPreview > 0 ? "work_in_progress" : "approved";

  function setTier2Elected(itemId: string, elected: boolean) {
    setTier2Map((prev) => ({
      ...prev,
      [itemId]: { ...(prev[itemId] ?? emptyTier2Response()), elected },
    }));
  }

  async function decide(sendToService: boolean) {
    setBusy(true);
    setError(null);
    const r = await reviewFulfillmentIntakeForm({
      form_id: form.id,
      reviewer_notes: notes,
      tier_2_responses: useStructured ? tier2Map : undefined,
      send_to_service: sendToService,
    });
    setBusy(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    onSaved();
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-end justify-center bg-black/60 p-2 sm:items-center sm:p-4"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-t-2xl border border-gray-200 bg-white p-5 shadow-xl dark:border-zinc-700 dark:bg-zinc-900 sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-bold text-gray-900 dark:text-zinc-100">Review intake form</h3>
        <p className="mt-1 text-xs text-gray-500 dark:text-zinc-400">
          Form id <code>{form.id.slice(0, 8)}</code> · status <code>{form.status}</code>
          {template ? (
            <>
              {" "}
              · template <code>{template.slug}</code>
            </>
          ) : (
            <>
              {" "}
              · <span className="text-amber-700 dark:text-amber-400">no matching template</span>
            </>
          )}
        </p>

        <div className="mt-4 space-y-4">
          {useStructured ? (
            <>
              <ReviewTier1Section items={tier1Items} responses={form.tier_1_responses} />
              <ReviewTier2Section
                items={tier2Items}
                responses={tier2Map}
                onToggle={setTier2Elected}
              />
              {form.tier_3_response ? (
                <div className="rounded-md border border-gray-200 bg-gray-50 p-3 dark:border-zinc-700 dark:bg-zinc-800">
                  <p className="text-sm font-medium text-gray-700 dark:text-zinc-200">
                    Free-form Tier 3
                  </p>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-gray-700 dark:text-zinc-300">
                    {form.tier_3_response}
                  </p>
                </div>
              ) : null}
            </>
          ) : (
            <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-xs dark:border-zinc-700 dark:bg-zinc-800">
              <p className="font-medium text-gray-700 dark:text-zinc-200">Submitted Tier 1</p>
              <pre className="mt-1 whitespace-pre-wrap break-all font-mono text-[11px] text-gray-700 dark:text-zinc-300">
                {JSON.stringify(form.tier_1_responses, null, 2)}
              </pre>
              <p className="mt-3 font-medium text-gray-700 dark:text-zinc-200">Submitted Tier 2</p>
              <pre className="mt-1 whitespace-pre-wrap break-all font-mono text-[11px] text-gray-700 dark:text-zinc-300">
                {JSON.stringify(form.tier_2_responses, null, 2)}
              </pre>
              {form.tier_3_response ? (
                <>
                  <p className="mt-3 font-medium text-gray-700 dark:text-zinc-200">
                    Free-form Tier 3
                  </p>
                  <p className="mt-1 whitespace-pre-wrap text-gray-700 dark:text-zinc-300">
                    {form.tier_3_response}
                  </p>
                </>
              ) : null}
            </div>
          )}

          <label className="block">
            <span className="block text-sm font-medium text-gray-700 dark:text-zinc-300">
              Reviewer notes
            </span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="mt-1 w-full rounded-md border border-gray-300 bg-white p-2 text-sm dark:border-zinc-700 dark:bg-zinc-950"
            />
          </label>

          {!useStructured ? (
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 dark:text-zinc-300">
                Tier-2 items elected for work (count)
              </span>
              <input
                type="number"
                min={0}
                value={legacyElectedCount}
                onChange={(e) =>
                  setLegacyElectedCount(Math.max(0, Number(e.target.value) || 0))
                }
                className="mt-1 w-32 rounded-md border border-gray-300 bg-white p-2 text-sm dark:border-zinc-700 dark:bg-zinc-950"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-zinc-400">
                Legacy fallback path — server still derives the routing decision from
                the form&apos;s saved <code>tier_2_responses</code>.
              </p>
            </label>
          ) : null}

          <p className="text-xs text-gray-500 dark:text-zinc-400">
            {electedCountPreview} item{electedCountPreview === 1 ? "" : "s"} elected for work · will
            route to <code>{willRouteTo}</code> on save.
            {electedCountPreview > 0 ? (
              <>
                {" "}
                Use <strong>Send to Service</strong> when upgrades need the Service department
                (intake stays in progress until the Service event completes).
              </>
            ) : null}
          </p>

          {error ? (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
              {error}
            </div>
          ) : null}
        </div>

        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-900"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void decide(false)}
            disabled={busy}
            className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {busy ? "Saving…" : electedCountPreview > 0 ? "Send to work" : "Approve"}
          </button>
          {electedCountPreview > 0 ? (
            <button
              type="button"
              onClick={() => void decide(true)}
              disabled={busy}
              className="rounded-md border border-indigo-400 bg-indigo-50 px-3 py-1.5 text-sm font-semibold text-indigo-900 hover:bg-indigo-100 disabled:opacity-50 dark:border-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-100"
            >
              {busy ? "Saving…" : "Send to Service"}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
